// src/services/chatService.js
import { db } from '../lib/firebase';
import {
  doc,
  setDoc,
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  getDoc,
  updateDoc,
  runTransaction,
  where,
  getDocs,
  arrayUnion
} from '../lib/firestoreClient';
import { sanitizeText } from '../utils/sanitize';
import { isChatVisibleForUser } from '../utils/unread';
import { errorDev } from '../utils/logger.js';

const CHATS = 'chats';
const MAX_MESSAGE_LENGTH = 2000;
const MAX_ATTACHMENTS = 5;
const SUPPORT_CONTEXT = 'support';

const formatOfferPreview = (amount) => {
  if (amount == null) return 'Offer proposed';
  const n = Number(amount);
  if (!Number.isFinite(n)) return 'Offer proposed';
  return `Offer proposed: ${n.toLocaleString()} RWF`;
};

const sanitizeAttachments = (items = []) => {
  if (!Array.isArray(items)) return [];
  return items
    .slice(0, MAX_ATTACHMENTS)
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      return {
        name: sanitizeText(item.name || '', 120),
        type: sanitizeText(item.type || '', 80),
        url: sanitizeText(item.url || '', 1024),
      };
    })
    .filter((item) => item && item.url);
};

const sanitizeExtra = (input = {}) => {
  if (!input || typeof input !== 'object') return {};
  const sanitized = {
    contextType: sanitizeText(input.contextType || '', 32),
    initiatedBy: sanitizeText(input.initiatedBy || '', 32),
    offerId: sanitizeText(input.offerId || '', 120),
    offerAmount: input.offerAmount,
    systemInit: Boolean(input.systemInit),
    supportUserId: sanitizeText(input.supportUserId || '', 128),
  };

  // Firestore rejects undefined values in nested maps.
  Object.keys(sanitized).forEach((key) => {
    if (typeof sanitized[key] === 'undefined') {
      delete sanitized[key];
    }
  });

  return sanitized;
};

// Shared listener registries keep a single live subscription per uid/chatId
// so overlapping callers (e.g. multiple inbox mounts) do not fan out duplicates.
// Each caller receives its own callback registration and ref count; when the
// last caller unsubscribes the underlying listener is torn down.
const createRegistry = () => new Map();

const attachSharedListener = (registry, key, attachFn, callback, onErrorCallback = null) => {
  let entry = registry.get(key);
  if (!entry) {
    entry = { refCount: 0, unsub: null, callbacks: new Set(), errorCallbacks: new Set() };
    registry.set(key, entry);
  }

  entry.refCount += 1;

  const dispatch = (data) => {
    for (const cb of entry.callbacks) {
      try { cb(data); } catch (err) { errorDev('Operation failed:', err); }
    }
  };

  const reportError = (error) => {
    for (const callback of entry.errorCallbacks) {
      try { callback(error); } catch (err) { errorDev('Operation failed:', err); }
    }
  };

  entry.callbacks.add(callback);

  if (onErrorCallback) {
    entry.errorCallbacks.add(onErrorCallback);
  }

  if (entry.refCount === 1) {
    entry.unsub = attachFn(dispatch, reportError) || (() => {});
  }

  return () => {
    if (entry.refCount <= 0) return;
    entry.callbacks.delete(callback);
    if (onErrorCallback) entry.errorCallbacks.delete(onErrorCallback);
    entry.refCount -= 1;
    if (entry.refCount <= 0) {
      if (entry.unsub) {
        try { entry.unsub(); } catch (_) {}
      }
      registry.delete(key);
    }
  };
};

const messageListenerRegistry = createRegistry();
const chatListenerRegistry = createRegistry();

// Helpers
export const sanitizeId = (v) => {
  if (!v && v !== 0) return null;
  const s = String(v);
  if (s.includes('/')) return s.split('/').pop();
  return s;
};

export const convoId = (a, b, contextId = null, contextType = null) => {
  const pa = String(a || '');
  const pb = String(b || '');
  const ids = [pa, pb].slice().sort().join('_');
  const cid = sanitizeId(contextId);

  if (cid) {
    return `product_${cid}_${ids}`;
  }
  return ids;
};

export const supportChatIdForUser = (userId) => {
  const uid = sanitizeId(userId);
  if (!uid) throw new Error('userId is required');
  return `support_${uid}`;
};

const resolveRecipientId = ({ participants = [], senderId, chatMeta = {} }) => {
  const sender = String(senderId || '');
  const supportUserId = chatMeta?.supportUserId ? String(chatMeta.supportUserId) : null;
  const isSupportChat = chatMeta?.contextType === SUPPORT_CONTEXT;

  if (isSupportChat && supportUserId) {
    if (sender !== supportUserId) return supportUserId;
    return participants.find((p) => String(p) !== sender) || null;
  }

  return participants.find((p) => String(p) !== sender) || null;
};

export const __chatInternals = {
  resolveRecipientId,
};

export const createOrGetSupportChat = async ({ userId, agentId, openingMessage = '', agentLabel = 'Support Center' }) => {
  const uid = sanitizeId(userId);
  const aid = sanitizeId(agentId);
  if (!uid || !aid) throw new Error('userId and agentId are required');

  const chatId = supportChatIdForUser(uid);
  const chatRef = doc(db, CHATS, chatId);
  const participants = Array.from(new Set([String(uid), String(aid)])).sort();
  const existingSnap = await getDoc(chatRef);
  const isNewSupportChat = !existingSnap.exists();

  await setDoc(chatRef, {
    participants,
    contextType: SUPPORT_CONTEXT,
    supportUserId: String(uid),
    supportAgentIds: arrayUnion(String(aid)),
    supportLabel: sanitizeText(agentLabel || 'Support Center', 80),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });

  const openingText = sanitizeText(openingMessage, MAX_MESSAGE_LENGTH);
  if (openingText && isNewSupportChat) {
    await sendMessage({
      chatId,
      senderId: String(aid),
      text: openingText,
      type: 'text',
      extra: {
        contextType: SUPPORT_CONTEXT,
        supportUserId: String(uid),
        initiatedBy: 'support_agent',
      },
    });
  }

  return { chatId };
};

export const getSupportChatForUser = async (userId) => {
  const uid = sanitizeId(userId);
  if (!uid) return null;

  // Primary stable path: support chats use deterministic ids.
  try {
    const canonicalId = supportChatIdForUser(uid);
    const canonicalSnap = await getDoc(doc(db, CHATS, canonicalId));
    if (canonicalSnap.exists()) {
      const row = { id: canonicalSnap.id, ...canonicalSnap.data() };
      if (row.contextType === SUPPORT_CONTEXT && String(row.supportUserId || '') === String(uid)) {
        return row;
      }
    }
  } catch (err) {
    // Continue to legacy query fallback below.
  errorDev("Operation failed:", err);
  }

  const pickSupportRow = (snap) => snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .find((chat) => chat.contextType === SUPPORT_CONTEXT && String(chat.supportUserId || '') === String(uid));

  try {
    const qIndexed = query(
      collection(db, CHATS),
      where('participants', 'array-contains', String(uid)),
      orderBy('updatedAt', 'desc'),
      limit(50)
    );
    const snap = await getDocs(qIndexed);
    return pickSupportRow(snap) || null;
  } catch (err) {
    const code = String(err?.code || '').toLowerCase();
    if (code === 'permission-denied' || code === 'failed-precondition') {
      try {
        // Fallback avoids index requirements and prevents hard failures in UX.
        const qFallback = query(
          collection(db, CHATS),
          where('participants', 'array-contains', String(uid)),
          limit(50)
        );
        const snapFallback = await getDocs(qFallback);
        return pickSupportRow(snapFallback) || null;
      } catch (err) {
        errorDev("Operation failed:", err);
        return null;
      }
    }
    // Keep support chat opener resilient: fallback callers can route to generic inbox.
    return null;
  }
};

export const listenSupportChatsForAdmin = (cb, onError = null) => {
  const reportError = (error) => {
    if (onError) onError(error);
    else errorDev('Support chat listener failed:', error);
  };

  try {
    const qIndexed = query(collection(db, CHATS), where('contextType', '==', SUPPORT_CONTEXT), orderBy('updatedAt', 'desc'), limit(100));
    return onSnapshot(qIndexed, (snap) => {
      cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (error) => {
      reportError(error);
      cb([]);
    });
  } catch (err) {
    errorDev("Operation failed:", err);
    try {
      const qFallback = query(collection(db, CHATS), where('contextType', '==', SUPPORT_CONTEXT), limit(100));
      return onSnapshot(qFallback, (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const aTs = a?.updatedAt?.seconds || 0;
            const bTs = b?.updatedAt?.seconds || 0;
            return bTs - aTs;
          });
        cb(rows);
      }, (error) => {
        reportError(error);
        cb([]);
      });
    } catch (err) {
      errorDev("Operation failed:", err);
      reportError(err);
      cb([]);
      return () => {};
    }
  }
};

export const sendSupportMessage = async ({ chatId, senderId, text = '' }) => {
  const cid = sanitizeId(chatId) || chatId;
  const meta = await getChatMeta(cid);
  if (!meta || meta.contextType !== SUPPORT_CONTEXT) throw new Error('Support chat not found');

  const sid = String(senderId);
  const supportUserId = String(meta.supportUserId || '');

  if (sid && sid !== supportUserId) {
    try {
      await updateDoc(doc(db, CHATS, cid), {
        supportAgentIds: arrayUnion(sid),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      // silent
    errorDev("Operation failed:", err);
    }
  }

  return sendMessage({
    chatId: cid,
    senderId: sid,
    text,
    type: 'text',
    extra: {
      contextType: SUPPORT_CONTEXT,
      supportUserId,
    },
  });
};

const tryExtractParticipantsFromChatId = (chatId) => {
  if (!chatId) return null;
  try {
    const decoded = decodeURIComponent(String(chatId));

    if (decoded.startsWith('product_')) {
      const parts = decoded.split('_');
      return parts.length >= 4 ? [parts[2], parts[3]] : null;
    } else {
      const parts = decoded.split('_');
      return parts.length >= 2 ? [parts[0], parts[1]] : null;
    }
  } catch (e) {
    // ignore
  }
  return null;
};

/* -------------------------
   Basic chat helpers (backwards compatible)
   ------------------------- */

export const findOrCreateChat = async (uidA, uidB, productId = null) => {
  const id = convoId(uidA, uidB, productId, 'product');
  const convoRef = doc(db, CHATS, id);
  const participants = [String(uidA), String(uidB)].sort();

  await setDoc(convoRef, {
    participants,
    productId: sanitizeId(productId) || null,
    contextType: productId ? 'product' : null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  }, { merge: true });

  return { chatId: id };
};

export const findOrCreateChatWithContext = async ({
  uidA, uidB, productId = null, contextType = null,
}) => {
  const contextKey = productId;
  const contextTypeFinal = productId ? 'product' : contextType;

  const id = convoId(uidA, uidB, contextKey, contextTypeFinal);
  const convoRef = doc(db, CHATS, id);
  const participants = [String(uidA), String(uidB)].sort();

  await setDoc(convoRef, {
    participants,
    productId: sanitizeId(productId) || null,
    contextType: contextTypeFinal,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  }, { merge: true });

  return { chatId: id };
};

export const getChatMeta = async (chatId) => {
  const cid = sanitizeId(chatId) || chatId;
  const ref = doc(db, CHATS, cid);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/**
 * Live chat-metadata listener. PaymentRequestBubble previously built
 * `onSnapshot(doc(db, "chats", chatId))` inline to watch deal
 * confirmations; this moves that subscription behind the service so the
 * component no longer imports Firestore. Shares the same document ref
 * and error contract as `getChatMeta`.
 *
 * @param {string} chatId
 * @param {Function} callback receives the mapped chat row (or null).
 * @param {Function} [onError] optional error handler.
 * @returns {Function} unsubscribe.
 */
export const subscribeChatMeta = (chatId, cb, onError = null) => {
  const cid = sanitizeId(chatId) || chatId;
  if (!cid) {
    cb(null);
    return () => {};
  }
  return onSnapshot(
    doc(db, CHATS, cid),
    (snap) => {
      cb(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    },
    (error) => {
      errorDev("chatService: chat meta listener failed", error);
      if (onError) onError(error);
    }
  );
};

/**
 * One-shot preview of the most recent message in a conversation.
 * `chatUtils.getLastMessagePreview` previously built the messages
 * subcollection query inline; it now lives beside the other message
 * readers so the utility module no longer imports Firestore.
 *
 * @param {string} chatId
 * @returns {Promise<{id:string}|null>} latest message row, or null.
 */
export const getLastMessagePreview = async (chatId) => {
  const cid = sanitizeId(chatId) || chatId;
  if (!cid) return null;
  try {
    const snap = await getDocs(
      query(collection(db, CHATS, cid, "messages"), orderBy("createdAt", "desc"), limit(1))
    );
    if (snap.empty) return null;
    const docSnap = snap.docs[0];
    return { id: docSnap.id, ...docSnap.data() };
  } catch (err) {
    errorDev("chatService: last message preview failed", err);
    return null;
  }
};

export const sendMessage = async ({ chatId, senderId, text = '', attachments = [], type = 'text', extra = {} }) => {
  const cid = sanitizeId(chatId) || chatId;
  const chatRef = doc(db, CHATS, cid);
  let chatSnap = await getDoc(chatRef);
  const sanitizedText = sanitizeText(text, MAX_MESSAGE_LENGTH);
  const sanitizedAttachments = sanitizeAttachments(attachments);
  const safeExtra = sanitizeExtra(extra);

  if (!chatSnap.exists()) {
    let inferred = tryExtractParticipantsFromChatId(cid) || [];
    if (!inferred.includes(String(senderId))) inferred = Array.from(new Set([String(senderId), ...inferred]));
    if (inferred.length === 0) inferred = [String(senderId)];
    const participantsCreated = inferred.sort();
    try {
      await setDoc(chatRef, {
        participants: participantsCreated,
        productId: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });
      chatSnap = await getDoc(chatRef);
    } catch (err) {
      // silent
    errorDev("Operation failed:", err);
    }
  }

  const chatMeta = chatSnap.exists() ? (chatSnap.data() || {}) : {};
  const participantsFromSnap = (chatSnap.exists() && Array.isArray(chatMeta.participants))
    ? chatSnap.data().participants
    : [String(senderId)];

  const recipientId = Array.isArray(participantsFromSnap)
    ? resolveRecipientId({ participants: participantsFromSnap, senderId, chatMeta })
    : null;

  if (type === 'offer' && safeExtra && safeExtra.offerAmount != null) {
    const n = Number(safeExtra.offerAmount);
    if (Number.isFinite(n)) {
      safeExtra.offerAmount = n;
    } else {
      delete safeExtra.offerAmount;
    }
  }

  const payload = {
    senderId,
    recipientId,
    text: sanitizedText,
    attachments: sanitizedAttachments,
    type,
    extra: safeExtra,
    createdAt: serverTimestamp()
  };

  const ref = await addDoc(collection(db, CHATS, cid, 'messages'), payload);

  try {
    const updates = {
      lastMessage: sanitizedText || (type === 'offer' ? formatOfferPreview(safeExtra?.offerAmount) : ''),
      lastSender: senderId,
      lastMessageType: type,
      updatedAt: serverTimestamp()
    };

    if (recipientId) {
      updates[`unread.${recipientId}`] = serverTimestamp();
    }
    updates[`readBy.${senderId}`] = serverTimestamp();

    if (type === 'offer') {
      if (safeExtra?.offerAmount != null) updates.lastOfferAmount = safeExtra.offerAmount;
      if (safeExtra?.offerId) updates.lastOfferId = safeExtra.offerId;
    }

    if (safeExtra?.contextType === SUPPORT_CONTEXT) {
      if (safeExtra?.supportUserId) updates.supportUserId = safeExtra.supportUserId;
      updates.contextType = SUPPORT_CONTEXT;
      if (senderId && String(senderId) !== String(safeExtra?.supportUserId || '')) {
        updates.supportAgentIds = arrayUnion(String(senderId));
      }
    }

    await updateDoc(chatRef, updates);
  } catch (err) {
    // silent
  errorDev("Operation failed:", err);
  }

  return { id: ref.id, ...payload };
};

export const listenMessages = (chatId, cb, onError = null) => {
  const cid = sanitizeId(chatId) || chatId;
  if (!cid) return () => {};
  return attachSharedListener(messageListenerRegistry, cid, (dispatch, reportError) => {
    const messagesBySource = new Map();
    const sourceErrors = new Map();
    const listeners = [];

    const emit = () => {
      const merged = new Map();
      for (const rows of messagesBySource.values()) {
        rows.forEach((row) => merged.set(row.id, row));
      }
      const rows = Array.from(merged.values()).sort((a, b) => {
        const aTime = a.createdAt || a.timestamp || 0;
        const bTime = b.createdAt || b.timestamp || 0;
        const toMillis = (value) => value?.toMillis?.() || (value?.seconds ? value.seconds * 1000 : new Date(value).getTime() || 0);
        return toMillis(aTime) - toMillis(bTime);
      });
      dispatch(rows);
    };

    const reportSourceError = (source, error) => {
      sourceErrors.set(source, error);
      if (sourceErrors.size === 2) reportError(error);
    };

    const attach = (source, field) => {
      try {
        const q = query(
          collection(db, CHATS, cid, 'messages'),
          orderBy(field, 'asc'),
          limit(200)
        );
        listeners.push(onSnapshot(q, (snap) => {
          messagesBySource.set(source, snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          sourceErrors.delete(source);
          emit();
        }, (error) => reportSourceError(source, error)));
      } catch (err) {
        reportSourceError(source, err);
      }
    };

    try {
      attach('createdAt', 'createdAt');
      attach('timestamp', 'timestamp');
      return () => listeners.forEach((unsubscribe) => unsubscribe());
    } catch (err) {
      errorDev("Operation failed:", err);
      reportError(err);
      return () => {};
    }
  }, cb, onError);
};

export const listenChatsForUser = (uid, cb, onError = null) => {
  if (!uid) {
    return () => {};
  }
  const uidClean = sanitizeId(uid) || uid;

  const reportError = (error) => {
    if (onError) onError(error);
    else errorDev('Chat list listener failed:', error);
  };

  const attachSnapshot = (dispatch, report) => {
    const q = query(
      collection(db, CHATS),
      where('participants', 'array-contains', uidClean),
      orderBy('updatedAt', 'desc'),
      limit(100),
    );
    let unsub = null;
    let fallbackAttached = false;

    const attachFallback = async () => {
      if (fallbackAttached) return;
      fallbackAttached = true;
      // Tear down the failed listener before attaching the fallback so we
      // never accumulate duplicate fallback listeners on repeated errors.
      if (unsub) {
        try { unsub(); } catch (_) {}
        unsub = null;
      }
      try {
        const qFallback = query(
          collection(db, CHATS),
          where('participants', 'array-contains', uidClean),
          limit(100)
        );
        const snap2 = await getDocs(qFallback);
        dispatch(
          snap2.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter((row) => isChatVisibleForUser(row, uidClean))
        );
        return onSnapshot(qFallback, (snap3) => {
          dispatch(
            snap3.docs
              .map(d => ({ id: d.id, ...d.data() }))
              .filter((row) => isChatVisibleForUser(row, uidClean))
          );
        }, (err) => {
          errorDev('Chat fallback snapshot error:', err);
          report(err);
          try { dispatch([]); } catch (_) {}
        });
      } catch (err) {
        // silent — getDocs fallback also failed
        report(err);
        return null;
      }
    };

    unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter((row) => isChatVisibleForUser(row, uidClean));
      dispatch(rows);
    }, async (err) => {
      report(err);
      // Only fall back for failed-precondition (missing composite index).
      // If permission-denied, the fallback getDocs would also fail since
      // the rules evaluate the same way — return empty instead of retrying.
      if (err && err.code === 'failed-precondition') {
        const fallbackUnsub = await attachFallback();
        if (fallbackUnsub) unsub = fallbackUnsub;
        return;
      }
      // permission-denied: keep the current inbox rendering, but the caller has
      // already been told this is a failure and not an empty conversation list.
      if (err && err.code === 'permission-denied') {
        try { dispatch([]); } catch (_) {}
      }
    });
    return unsub;
  };

  return attachSharedListener(chatListenerRegistry, uidClean, (dispatch, report) => {
    try {
      return attachSnapshot(dispatch, report);
    } catch (err) {
      errorDev("Operation failed:", err);
      report(err);
      try { dispatch([]); } catch (_) {}
      return () => {};
    }
  }, cb, onError);
};

export const markChatRead = async (chatId, uid) => {
  const cid = sanitizeId(chatId) || chatId;
  const ref = doc(db, CHATS, cid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  await updateDoc(ref, {
    [`readBy.${uid}`]: serverTimestamp(),
  });
};

export const setTyping = async (chatId, uid, isTyping) => {
  const cid = sanitizeId(chatId) || chatId;
  await updateDoc(doc(db, CHATS, cid), { [`typing.${uid}`]: !!isTyping, updatedAt: serverTimestamp() });
};

export const listenMessagesInConversation = (conversationId, cb) => {
  if (!conversationId) return () => {};
  let chatId;
  try { chatId = decodeURIComponent(String(conversationId)); } catch (err) { errorDev("Operation failed:", err);  chatId = String(conversationId); }
  return listenMessages(chatId, cb);
};

export const sendMessageToConversation = async ({ conversationId, senderId, text = '', attachments = [], type = 'text', extra = {} }) => {
  let chatId;
  try { chatId = decodeURIComponent(String(conversationId)); } catch (err) { errorDev("Operation failed:", err);  chatId = String(conversationId); }
  return sendMessage({ chatId, senderId, text, attachments, type, extra });
};

const deriveChatPreviewFromMessage = (msg) => {
  if (!msg || msg.isDeleted) return '';
  if (msg.type === 'offer') {
    const amount = msg?.extra?.offerAmount;
    return formatOfferPreview(amount);
  }
  if (msg.type === 'location') return 'Shared location';
  return sanitizeText(msg.text || '', MAX_MESSAGE_LENGTH);
};

const refreshChatMetaAfterMessageDelete = async (chatId) => {
  const cid = sanitizeId(chatId) || chatId;
  const chatRef = doc(db, CHATS, cid);

  const recentSnap = await getDocs(
    query(collection(db, CHATS, cid, 'messages'), orderBy('createdAt', 'desc'), limit(25))
  );

  const visibleLast = recentSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .find((row) => !row?.isDeleted);

  const updates = {
    updatedAt: serverTimestamp(),
    lastMessage: deriveChatPreviewFromMessage(visibleLast),
    lastMessageType: visibleLast?.type || 'text',
    lastSender: visibleLast?.senderId || null,
  };

  await updateDoc(chatRef, updates);
};

export const deleteMessageForUser = async ({ chatId, messageId, userId }) => {
  if (!chatId || !messageId || !userId) throw new Error('chatId, messageId, and userId are required');

  const cid = sanitizeId(chatId) || chatId;
  const chatRef = doc(db, CHATS, cid);
  const msgRef = doc(db, CHATS, cid, 'messages', String(messageId));

  await runTransaction(db, async (tx) => {
    const [chatSnap, msgSnap] = await Promise.all([tx.get(chatRef), tx.get(msgRef)]);
    if (!chatSnap.exists()) throw new Error('Chat not found');
    if (!msgSnap.exists()) throw new Error('Message not found');

    const chat = chatSnap.data() || {};
    const participants = Array.isArray(chat.participants) ? chat.participants.map(String) : [];
    if (!participants.includes(String(userId))) throw new Error('Not a chat participant');

    const msg = msgSnap.data() || {};
    if (String(msg.senderId) !== String(userId)) throw new Error('Only the message owner can delete this message');
    if (msg.isDeleted) return;

    tx.update(msgRef, {
      isDeleted: true,
      deletedBy: String(userId),
      deletedAt: serverTimestamp(),
      text: '',
      attachments: [],
      updatedAt: serverTimestamp(),
    });
  });

  await refreshChatMetaAfterMessageDelete(cid);
};

export const deleteConversationForUser = async ({ chatId, userId }) => {
  if (!chatId || !userId) throw new Error('chatId and userId are required');

  const cid = sanitizeId(chatId) || chatId;
  const chatRef = doc(db, CHATS, cid);

  await runTransaction(db, async (tx) => {
    const chatSnap = await tx.get(chatRef);
    if (!chatSnap.exists()) throw new Error('Chat not found');
    const chat = chatSnap.data() || {};
    const participants = Array.isArray(chat.participants) ? chat.participants.map(String) : [];
    if (!participants.includes(String(userId))) throw new Error('Not a chat participant');

    tx.update(chatRef, {
      [`deletedFor.${String(userId)}`]: serverTimestamp(),
      [`readBy.${String(userId)}`]: serverTimestamp(),
    });
  });
};
